import { Calendar, CircleDollarSign, PieChart, User } from 'lucide-preact';
import type { ComponentChildren } from 'preact';
import { Card, LoadingState } from '../../components/ui';

interface UnitKpiSectionProps {
  totalCost: string;
  yearsHeld: string;
  portfolioPercentage: number;
  ownership: number;
  unitCurrency: string;
  loading: boolean;
}

function formatPercent(value: number) {
  if (!Number.isFinite(value)) return '—';
  const rounded = Math.round(value * 10) / 10;
  return `${Number.isInteger(rounded) ? rounded.toFixed(0) : rounded.toFixed(1)}%`;
}

function formatNumericText(value: string) {
  const numericMatch = value.match(/-?[\d,]+(?:\.\d+)?/);
  if (!numericMatch) return value;

  const numericValue = Number(numericMatch[0].replace(/,/g, ''));
  if (!Number.isFinite(numericValue)) return value;

  const formattedValue = numericValue.toLocaleString('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 1,
  });
  return value.replace(numericMatch[0], formattedValue);
}

function formatYearsValue(yearsHeld: string) {
  return formatNumericText(yearsHeld.replace(/\s*years?$/i, '').trim() || '0');
}

function SummaryStat({
  icon,
  value,
  label,
}: {
  icon: ComponentChildren;
  value: string;
  label: string;
}) {
  return (
    <div className="unit-summary__stat">
      <span className="unit-summary__stat-icon" aria-hidden="true">
        {icon}
      </span>
      <div className="unit-summary__stat-text">
        <span className="unit-summary__stat-value">{value}</span>
        <span className="unit-summary__stat-label">{label}</span>
      </div>
    </div>
  );
}

export function UnitKpiSection({
  totalCost,
  yearsHeld,
  portfolioPercentage,
  ownership,
  unitCurrency,
  loading,
}: UnitKpiSectionProps) {
  return (
    <Card as="section" padding="flush" className="unit-summary">
      <div className="unit-summary__head">
        <div className="unit-summary__heading">
          <span className="unit-summary__head-icon" aria-hidden="true">
            <CircleDollarSign size={16} strokeWidth={2} />
          </span>
          <h2 className="unit-summary__title">Investment Summary</h2>
        </div>
        <span className="unit-summary__currency">{unitCurrency || 'USD'}</span>
      </div>

      {loading ? (
        <LoadingState message="Loading investment summary…" className="unit-summary__loading" />
      ) : (
        <>
          <div className="unit-summary__hero">
            <div className="unit-summary__hero-copy">
              <span className="unit-summary__hero-label">Total Cost</span>
              <span className="unit-summary__hero-value">{formatNumericText(totalCost)}</span>
            </div>
          </div>

          <div className="unit-summary__stats">
            <SummaryStat
              icon={<Calendar size={14} strokeWidth={2} />}
              value={formatYearsValue(yearsHeld)}
              label="Years Held"
            />
            <SummaryStat
              icon={<PieChart size={14} strokeWidth={2} />}
              value={formatPercent(portfolioPercentage)}
              label="% Of Portfolio"
            />
            <SummaryStat
              icon={<User size={14} strokeWidth={2} />}
              value={formatPercent(ownership)}
              label="% Ownership"
            />
          </div>
        </>
      )}
    </Card>
  );
}
